import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAssetVendor,
  useCreateAssetVendor,
  useUpdateAssetVendor,
} from '../../../services/httpServices/queries';
import { ExpensesLayout } from '../expenses/ExpensesLayout';
import { Loader2, AlertCircle, Save, ArrowLeft, Truck } from 'lucide-react';

export function AssetVendorForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { data, isLoading } = useAssetVendor(id);
  const existing = data?.data;

  const createVendor = useCreateAssetVendor();
  const updateVendor = useUpdateAssetVendor();

  const [form, setForm] = useState({
    name: '',
    vendorCode: '',
    contactPerson: '',
    phoneNumber: '',
    email: '',
    location: '',
    city: '',
    address: '',
    taxId: '',
    paymentTermsDays: 0,
    notes: '',
    isActive: true,
  });
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setForm({
        name: existing.name || '',
        vendorCode: existing.vendorCode || '',
        contactPerson: existing.contactPerson || '',
        phoneNumber: existing.phoneNumber || '',
        email: existing.email || '',
        location: existing.location || '',
        city: existing.city || '',
        address: existing.address || '',
        taxId: existing.taxId || '',
        paymentTermsDays: existing.paymentTermsDays ?? 0,
        notes: existing.notes || '',
        isActive: existing.isActive ?? true,
      });
    }
  }, [existing]);

  const set = (patch: Partial<typeof form>) => setForm((prev) => ({ ...prev, ...patch }));

  const isSubmitting = createVendor.isPending || updateVendor.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!form.name.trim()) return setFormError('Vendor name is required');

    const payload = {
      name: form.name.trim(),
      vendorCode: form.vendorCode.trim() || undefined,
      contactPerson: form.contactPerson || undefined,
      phoneNumber: form.phoneNumber || undefined,
      email: form.email || undefined,
      location: form.location || undefined,
      city: form.city || undefined,
      address: form.address || undefined,
      taxId: form.taxId || undefined,
      paymentTermsDays: Number(form.paymentTermsDays) || 0,
      notes: form.notes || undefined,
      isActive: form.isActive,
    };

    const onSuccess = () => navigate('/billing/expenses/vendors');
    const onError = (err: any) =>
      setFormError(err?.details?.map((d: any) => d.message).join(', ') || err?.message || 'Failed to save vendor');

    if (isEdit && id) {
      updateVendor.mutate({ id, data: payload }, { onSuccess, onError });
    } else {
      createVendor.mutate(payload, { onSuccess, onError });
    }
  };

  if (isEdit && isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <ExpensesLayout
      title={isEdit ? 'Edit Vendor' : 'Add Vendor'}
      description="Supplier details and location used on asset invoices"
      helpKey="/billing/expenses/vendors/form"
      action={
        <button
          onClick={() => navigate('/billing/expenses/vendors')}
          className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2"
        >
          <ArrowLeft size={16} />
          Back
        </button>
      }
    >
      <div className="bg-surface rounded-xl border border-border p-4 max-w-3xl">
        <div className="flex items-center gap-2 mb-4">
          <Truck size={18} className="text-primary" />
          <h2 className="text-base font-semibold text-text">Vendor Details</h2>
        </div>

        {formError && (
          <div className="flex items-start gap-2 p-3 mb-4 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{formError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text mb-1">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="e.g. Siemens Bangladesh Ltd."
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Vendor Code</label>
              <input
                type="text"
                value={form.vendorCode}
                onChange={(e) => set({ vendorCode: e.target.value })}
                placeholder="Auto-generated if empty"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Contact Person</label>
              <input
                type="text"
                value={form.contactPerson}
                onChange={(e) => set({ contactPerson: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Phone</label>
              <input
                type="text"
                value={form.phoneNumber}
                onChange={(e) => set({ phoneNumber: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Location / Area</label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => set({ location: e.target.value })}
                placeholder="e.g. Tejgaon, Dhaka"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">City</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => set({ city: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Tax ID</label>
              <input
                type="text"
                value={form.taxId}
                onChange={(e) => set({ taxId: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Payment Terms (days)</label>
              <input
                type="number"
                min={0}
                value={form.paymentTermsDays}
                onChange={(e) => set({ paymentTermsDays: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-text mb-1">Address</label>
              <textarea
                rows={2}
                value={form.address}
                onChange={(e) => set({ address: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-text mb-1">Notes</label>
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => set({ notes: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="vendor-active"
              checked={form.isActive}
              onChange={(e) => set({ isActive: e.target.checked })}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
            />
            <label htmlFor="vendor-active" className="text-sm text-text cursor-pointer">
              Active
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={() => navigate('/billing/expenses/vendors')}
              className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {isEdit ? 'Save Changes' : 'Add Vendor'}
            </button>
          </div>
        </form>
      </div>
    </ExpensesLayout>
  );
}
